// Quality › Latin text (Mungun/infra text-conversion review, P1): what customers typed in Latin and what it became.
// No imports on purpose: `node --test` loads this file directly.

export interface TextChange {
  raw: string;
  out: string;
  kind: string;
}

export interface ConversionRow extends TextChange {
  count: number;
  lastSeen: string;
  samples: { question: string; normalized: string; at: string }[];
  /** A word of `out` is not in the converter's dictionary — look at it first (P2). */
  check?: boolean;
}

export interface ConverterVersion {
  converter: string;
  code: string | null;
  lexicon: string | null;
  count: number;
}

export interface Conversions {
  days: number;
  /** `since`: counted from the bot's first recorded conversion, when that is inside the window. */
  messages: { total: number; latin: number; since?: string | null };
  versions: ConverterVersion[];
  rows: ConversionRow[];
}

/** POST …/convert: what the converter makes of a text now. */
export interface ConvertResult {
  normalized: string;
  script: string;
  changes: TextChange[];
  suggestions: { raw: string; out: string }[];
  version: ConverterVersionInfo | null;
}

export interface ConverterVersionInfo {
  code: string;
  lexicon: string;
  /** The operator-corrections version the converter used (P2); "none" = none loaded. */
  corrections?: string;
}

/** One operator correction (P2): `latin` (1–3 lower-case words) is converted to `cyrillic`. */
export interface Correction {
  id: string;
  scope: 'bot' | 'global';
  system: string;
  botId: string;
  latin: string;
  cyrillic: string;
  enabled: boolean;
  suggestGlobal: boolean;
  note: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CorrectionChange {
  id: string;
  actor: string | null;
  before: Partial<Correction> | null;
  after: Partial<Correction>;
  createdAt: string;
}

const KIND_LABEL: Record<string, string> = {
  translit: 'Letters + dictionary',
  slang: 'Slang list',
  typo: 'Typo fix',
  correction: 'Correction',
  keep: 'Kept Latin',
};

/** How a word was converted, in plain words. */
export function kindLabel(kind: string): string {
  return KIND_LABEL[kind] ?? kind;
}

/** "12 of 340 messages the AI handled were typed in Latin (4%)". */
/**
 * A converted word an operator can keep Latin (P4): the hub glossary takes ≤ 80 characters with a Latin letter. Not a
 * corrected word: a kept word is never corrected, so keeping it would quietly undo the operator's own correction.
 */
export function canKeepLatin(row: { raw: string; kind: string }): boolean {
  return row.kind !== 'keep' && row.kind !== 'correction' && /[A-Za-z]/.test(row.raw) && row.raw.length <= 80;
}

export function latinShare(m: { total: number; latin: number; since?: string | null }): string {
  if (!m.total) return 'No messages handled by the AI in this period';
  const share = `${m.latin} of ${m.total} messages the AI handled were typed in Latin (${Math.round((m.latin / m.total) * 100)}%)`;
  return m.since ? `${share}, counted from ${m.since.slice(0, 10)}` : share;
}

function versionName(v: ConverterVersion): string {
  return v.code ? `${v.code}/${(v.lexicon ?? '?').slice(0, 8)}` : 'built-in copy';
}

/** One converter version is normal; more than one in the window means the converters drifted (spec D10). */
export function versionNote(versions: ConverterVersion[]): string | null {
  const names = [...new Set(versions.map(versionName))];
  if (names.length <= 1) return null;
  return `Converted by ${names.length} versions: ${versions.map((v) => `${versionName(v)} (${v.count})`).join(', ')}`;
}

/** The test box's error line: the converter being down reads as such. */
export function convertErrorText(message: string): string {
  return /converter unavailable/i.test(message) ? 'Converter unavailable, try again.' : message;
}

export function conversionsPath(bot: { system: string; botId: string }, days: number): string {
  return `/api/text/${encodeURIComponent(bot.system)}/${encodeURIComponent(bot.botId)}/conversions?days=${days}`;
}

export function convertPath(bot: { system: string; botId: string }): string {
  return `/api/text/${encodeURIComponent(bot.system)}/${encodeURIComponent(bot.botId)}/convert`;
}

export function correctionsPath(bot: { system: string; botId: string }, id?: string): string {
  const base = `/api/text/${encodeURIComponent(bot.system)}/${encodeURIComponent(bot.botId)}/corrections`;
  return id ? `${base}/${encodeURIComponent(id)}` : base;
}

/** A failed save, from the hub client's "<status> <text> — <body>" message, in words an operator can act on. */
export function correctionError(message: string): string {
  const status = Number(message.match(/^(\d{3})\b/)?.[1] ?? 0);
  if (status === 409) return 'This word already has a correction — edit it in Corrections';
  if (status === 403) return 'You need edit access to this bot';
  if (status === 400) {
    const i = message.indexOf(' — ');
    try {
      const reason = JSON.parse(message.slice(i + 3))?.message;
      if (typeof reason === 'string' && reason) return reason;
    } catch {
      /* not JSON */
    }
  }
  return 'Could not save, try again';
}

export function ruleSource(rule: { scope: string }): 'This bot' | 'All bots' {
  return rule.scope === 'global' ? 'All bots' : 'This bot';
}

/** "Converter 0.0.4, dictionary d9e15be7, corrections c0ffee12". */
export function versionLine(v: ConverterVersionInfo): string {
  const base = `Converter ${v.code}, dictionary ${v.lexicon.slice(0, 8)}`;
  if (!v.corrections) return base;
  return v.corrections === 'none' ? `${base}, no corrections loaded` : `${base}, corrections ${v.corrections.slice(0, 8)}`;
}

// ---- P3: checked conversions ---------------------------------------------------------------------------------------------

/** What a correction save did to the checked conversions (`null`: the converter could not be reached). */
export interface CorrectionEvaluation {
  cases: number;
  before: number;
  after: number;
  changed: { caseId: string; latin: string; expected: string; before: string; after: string }[];
}

export interface TextEvalCase {
  id: string;
  scope: 'bot' | 'global';
  system: string;
  botId: string;
  latin: string;
  expected: string;
  source: 'confirmed' | 'correction' | 'seed';
  createdBy: string | null;
  createdAt: string;
}

export interface TextEvalRun {
  id: string;
  trigger: string;
  cases: number;
  passed: number;
  failed: { caseId: string; got: string }[];
  lexiconVersion: string | null;
  correctionsVersion: string | null;
  createdBy: string | null;
  createdAt: string;
}

/** "Checked 12 conversions: 10 → 11 right". `undefined` (nothing measured) → "". */
export function accuracyText(e: { cases: number; before: number; after: number } | null | undefined): string {
  if (e === undefined) return '';
  if (e === null) return 'Could not check (converter unavailable)';
  if (!e.cases) return 'No checked conversions yet';
  return `Checked ${e.cases} conversion${e.cases === 1 ? '' : 's'}: ${e.before} → ${e.after} right`;
}

/** "97.2%" — share of cases the converter got right; "—" for an empty run. */
export function runAccuracy(run: { cases: number; passed: number }): string {
  if (!run.cases) return '—';
  return `${Math.round((run.passed / run.cases) * 1000) / 10}%`;
}

/** The hub's 409 asking to confirm an all-bots change, from the client's "<status> <text> — <body>" message. */
export function needsConfirm(message: string): { message: string; evaluation: CorrectionEvaluation | null } | null {
  if (!/^409\b/.test(message)) return null;
  try {
    const body = JSON.parse(message.slice(message.indexOf(' — ') + 3));
    return body?.needsConfirm === true ? { message: String(body.message ?? ''), evaluation: body.evaluation ?? null } : null;
  } catch {
    return null;
  }
}

/** The newest full (manual) run: a correction save only checks the cases its word touches. */
export function latestFullRun<T extends { trigger: string }>(runs: T[]): T | null {
  return runs.find((r) => r.trigger === 'manual') ?? null;
}

/** "All 40 cases, run by admin" / "2 cases touched by a correction saved by op". */
export function runLabel(run: { trigger: string; cases: number; createdBy: string | null }): string {
  const who = run.createdBy ?? '—';
  if (run.trigger === 'manual') return `All ${run.cases} cases, run by ${who}`;
  return `${run.cases} case${run.cases === 1 ? '' : 's'} touched by a correction saved by ${who}`;
}
